import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, CheckCircle, Search, ChevronDown, Tag, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/context/LanguageContext";

interface ShippingCity {
  id: string;
  name: string;
  name_arabic: string | null;
  shipping_fee: number;
}

const Checkout = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ customerName: "", customerEmail: "", customerPhone: "", shippingAddress: "", notes: "" });

  const [cities, setCities] = useState<ShippingCity[]>([]);
  const [selectedCity, setSelectedCity] = useState<ShippingCity | null>(null);
  const [citySearch, setCitySearch] = useState("");
  const [cityOpen, setCityOpen] = useState(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount_percentage: number } | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");

  const shippingFee = selectedCity?.shipping_fee || 0;
  const discountPercentage = appliedPromo?.discount_percentage || 0;
  const discountAmount = (cartTotal * discountPercentage) / 100;
  const orderTotal = cartTotal - discountAmount + shippingFee;

  const filteredCities = useMemo(() => {
    if (!citySearch) return cities;
    const q = citySearch.toLowerCase();
    return cities.filter(c => c.name.toLowerCase().includes(q) || c.name_arabic?.includes(q));
  }, [cities, citySearch]);

  useEffect(() => {
    const fetchCities = async () => {
      const { data } = await supabase.from("shipping_cities").select("id, name, name_arabic, shipping_fee").eq("is_active", true).order("name");
      if (data) setCities(data);
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        setFormData(prev => ({ ...prev, customerEmail: user.email || "" }));
        const { data: profile } = await supabase.from("profiles").select("full_name, phone, address").eq("user_id", user.id).single();
        if (profile) {
          setFormData(prev => ({ ...prev, customerName: profile.full_name || "", customerPhone: profile.phone || "", shippingAddress: profile.address || "" }));
        }
      }
    };
    fetchUserProfile();
  }, [user]);

  const applyPromoCode = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const { data, error } = await supabase
        .from("promo_codes")
        .select("code, discount_percentage, is_active, max_uses, times_used, expires_at")
        .eq("code", code)
        .eq("is_active", true)
        .single();

      if (error || !data) {
        setPromoError(language === "ar" ? "كود الخصم غير صالح" : "Invalid promo code");
        return;
      }

      if (data.max_uses && (data as any).times_used >= data.max_uses) {
        setPromoError(language === "ar" ? "تم استنفاد هذا الكود" : "This promo code has been fully used");
        return;
      }

      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setPromoError(language === "ar" ? "انتهت صلاحية هذا الكود" : "This promo code has expired");
        return;
      }

      setAppliedPromo({ code: data.code, discount_percentage: data.discount_percentage });
      setPromoInput("");
      toast({
        title: language === "ar" ? "تم تطبيق الخصم!" : "Promo applied!",
        description: language === "ar" ? `خصم ${data.discount_percentage}% على طلبك` : `${data.discount_percentage}% discount applied to your order`,
      });
    } catch {
      setPromoError(language === "ar" ? "كود الخصم غير صالح" : "Invalid promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated || !user) {
      toast({ title: t.checkout.signInRequired, description: t.checkout.signInRequiredDesc, variant: "destructive" });
      navigate("/login");
      return;
    }
    if (!formData.customerName || !formData.customerEmail || !formData.shippingAddress || !selectedCity) {
      toast({ title: t.checkout.missingInfo, description: t.checkout.missingInfoDesc, variant: "destructive" });
      return;
    }
    if (cartItems.length === 0) {
      toast({ title: t.checkout.emptyCartTitle, description: t.checkout.emptyCartDesc2, variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data: order, error: orderError } = await supabase.from("orders").insert({
        user_id: user.id, customer_name: formData.customerName, customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone, shipping_address: formData.shippingAddress,
        notes: formData.notes || null, total_amount: orderTotal, status: "pending",
        shipping_city: selectedCity.name, shipping_fee: shippingFee,
        promo_code: appliedPromo?.code || null,
        discount_percentage: discountPercentage,
        discount_amount: discountAmount,
      } as any).select().single();
      if (orderError) throw orderError;

      // Increment promo usage via database function
      if (appliedPromo) {
        const session = (await supabase.auth.getSession()).data.session;
        if (session) {
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/increment_promo_usage`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              "Authorization": `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ promo_code_value: appliedPromo.code }),
          }).catch(() => {});
        }
      }

      const orderItems = cartItems.map((item) => ({
        order_id: (order as any).id, product_id: item.product.id, product_name: item.product.name,
        size: item.selectedSize, unit_price: item.price, quantity: item.quantity,
      }));
      const { error: itemsError } = await supabase.from("order_items").insert(orderItems);
      if (itemsError) throw itemsError;
      setOrderId((order as any).id);
      setOrderComplete(true);
      clearCart();
      supabase.functions.invoke("notify-admin-order", {
        body: {
          orderId: (order as any).id, customerName: formData.customerName, customerEmail: formData.customerEmail,
          customerPhone: formData.customerPhone, shippingAddress: formData.shippingAddress,
          shippingCity: selectedCity.name, shippingFee, notes: formData.notes, totalAmount: orderTotal,
          promoCode: appliedPromo?.code || null, discountPercentage, discountAmount,
          items: cartItems.map((item) => ({ product_name: item.product.name, size: item.selectedSize, quantity: item.quantity, unit_price: item.price })),
        },
      }).catch((err) => console.error("Admin notification failed:", err));
      toast({ title: t.checkout.orderConfirmed, description: t.checkout.orderConfirmedDesc });
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({ title: t.checkout.orderFailed, description: error.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getCityLabel = (city: ShippingCity) => language === "ar" && city.name_arabic ? city.name_arabic : city.name;

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-lg mx-auto px-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-card border border-border rounded-sm p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-serif mb-2">{t.checkout.orderConfirmed}</h1>
            <p className="text-muted-foreground mb-4">{t.checkout.orderConfirmedDesc}</p>
            {orderId && <p className="text-sm text-muted-foreground mb-6">{t.checkout.orderId}: <span className="font-mono">{orderId.slice(0, 8)}</span></p>}
            <Button variant="bronze" onClick={() => navigate("/collection")}>{t.checkout.continueShopping}</Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h1 className="text-3xl font-serif mb-4">{t.checkout.emptyCart}</h1>
          <p className="text-muted-foreground mb-6">{t.checkout.emptyCartDesc}</p>
          <Button variant="bronze" onClick={() => navigate("/collection")}>{t.checkout.browseCollection}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        <Link to="/collection" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />{t.checkout.continueShopping}
        </Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="text-3xl font-serif mb-6">{t.checkout.title}</h1>
            {!isAuthenticated && (
              <div className="bg-muted/30 border border-border rounded-sm p-4 mb-6">
                <p className="text-sm text-muted-foreground"><Link to="/login" className="text-primary hover:underline">{t.login.signIn}</Link> {t.checkout.signInPrompt}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-xl font-serif mb-4">{t.checkout.contactInfo}</h2>
                <div className="space-y-4">
                  <div><label className="text-sm font-medium mb-1 block">{t.checkout.fullName}</label><Input value={formData.customerName} onChange={(e) => setFormData({ ...formData, customerName: e.target.value })} required /></div>
                  <div><label className="text-sm font-medium mb-1 block">{t.checkout.email}</label><Input type="email" value={formData.customerEmail} onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })} required /></div>
                  <div><label className="text-sm font-medium mb-1 block">{t.checkout.phone}</label><Input type="tel" value={formData.customerPhone} onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })} required /></div>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-serif mb-4">{t.checkout.shippingAddress}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      {language === "ar" ? "المدينة *" : "City *"}
                    </label>
                    <Popover open={cityOpen} onOpenChange={setCityOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                          {selectedCity ? getCityLabel(selectedCity) : (language === "ar" ? "اختر المدينة..." : "Select city...")}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <div className="p-2 border-b border-border">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input placeholder={language === "ar" ? "ابحث عن مدينة..." : "Search city..."} value={citySearch} onChange={(e) => setCitySearch(e.target.value)} className="pl-8" />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-1">
                          {filteredCities.length === 0 ? (
                            <p className="text-sm text-muted-foreground p-3 text-center">{language === "ar" ? "لم يتم العثور على مدن" : "No cities found"}</p>
                          ) : (
                            filteredCities.map((city) => (
                              <button key={city.id} type="button" onClick={() => { setSelectedCity(city); setCityOpen(false); setCitySearch(""); }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-sm hover:bg-muted transition-colors ${selectedCity?.id === city.id ? "bg-primary/10 text-primary" : ""}`}>
                                <span>{getCityLabel(city)}</span>
                                <span className="text-muted-foreground">{city.shipping_fee} {t.common.egp}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selectedCity && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {language === "ar" ? "رسوم الشحن" : "Shipping fee"}: {selectedCity.shipping_fee} {t.common.egp}
                      </p>
                    )}
                  </div>
                  <Textarea value={formData.shippingAddress} onChange={(e) => setFormData({ ...formData, shippingAddress: e.target.value })} placeholder={t.checkout.shippingPlaceholder} rows={3} required />
                </div>
              </div>

              {/* Promo Code Section */}
              <div>
                <h2 className="text-xl font-serif mb-4">
                  {language === "ar" ? "كود الخصم" : "Promo Code"}
                </h2>
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-sm p-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary" />
                      <span className="font-mono font-bold text-primary">{appliedPromo.code}</span>
                      <span className="text-sm text-muted-foreground">(-{appliedPromo.discount_percentage}%)</span>
                    </div>
                    <button type="button" onClick={removePromo} className="text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder={language === "ar" ? "أدخل كود الخصم..." : "Enter promo code..."}
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value); setPromoError(""); }}
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" onClick={applyPromoCode} disabled={promoLoading || !promoInput.trim()}>
                        {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === "ar" ? "تطبيق" : "Apply")}
                      </Button>
                    </div>
                    {promoError && <p className="text-sm text-destructive">{promoError}</p>}
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-xl font-serif mb-4">{t.checkout.orderNotes}</h2>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder={t.checkout.notesPlaceholder} rows={3} />
              </div>
              <Button type="submit" variant="bronze" size="lg" className="w-full" disabled={loading}>
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.checkout.processing}</>) : `${t.checkout.placeOrder} - ${orderTotal.toFixed(2)} ${t.common.egp}`}
              </Button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="bg-card border border-border rounded-sm p-6 sticky top-24">
              <h2 className="text-xl font-serif mb-6">{t.checkout.orderSummary}</h2>
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={`${item.product.id}-${item.selectedSize}`} className="flex gap-4">
                    <img src={item.product.image} alt={item.product.name} className="w-16 h-20 object-cover rounded-sm" />
                    <div className="flex-1">
                      <h3 className="font-serif">{item.product.name}</h3>
                      <p className="text-sm text-muted-foreground">{item.selectedSize}</p>
                      <p className="text-sm text-muted-foreground">{t.checkout.qty}: {item.quantity}</p>
                    </div>
                    <p className="font-medium">{(item.price * item.quantity).toFixed(2)} {t.common.egp}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-muted-foreground"><span>{t.checkout.subtotal}</span><span>{cartTotal.toFixed(2)} {t.common.egp}</span></div>
                {appliedPromo && (
                  <div className="flex justify-between text-primary">
                    <span>{language === "ar" ? "الخصم" : "Discount"} ({appliedPromo.discount_percentage}%)</span>
                    <span>-{discountAmount.toFixed(2)} {t.common.egp}</span>
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>{t.checkout.shipping}</span>
                  <span>{selectedCity ? `${shippingFee.toFixed(2)} ${t.common.egp}` : (language === "ar" ? "اختر المدينة" : "Select city")}</span>
                </div>
                <div className="flex justify-between text-lg font-serif pt-2 border-t border-border"><span>{t.checkout.total}</span><span>{orderTotal.toFixed(2)} {t.common.egp}</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
